import { useTranslation } from "react-i18next";
import { addMonths, isEqual, isPast, set as dateFnsSet } from "date-fns";
import { parseExpression } from "cron-parser";

import { updateSlide } from "store/common/common-slice";
import { useRootDispatch, useRootSelector } from "store/hooks";
import {
  currentAutoPay,
  getAccountRestrictions,
  selectedAccountInfo,
} from "store/account/account-selector";
import { selectAllschedulePayments } from "store/schedulePayments/schedulePayments-slice";
import { selectAllPaymentActivity } from "store/paymentActivity/paymentActivity-slice";

import classes from "./UpcomingPayments.module.scss";
import { useMemo } from "react";

export const UpcomingPayments = () => {
  const { t } = useTranslation(["common"]);
  const dispatch = useRootDispatch();

  const { applicationName } = useRootSelector((state) => state.config);
  const accountInfo = useRootSelector(selectedAccountInfo);
  const autoPayInfo = useRootSelector(currentAutoPay);
  const schedulePayments = useRootSelector(selectAllschedulePayments);
  const payments = useRootSelector(selectAllPaymentActivity);
  const { restrictions } = useRootSelector(getAccountRestrictions);

  const actionDisabled: boolean = useMemo(() => {
    return (
      accountInfo?.active !== true ||
      (restrictions.achRestricted && restrictions.cardRestricted)
    );
  }, [accountInfo, restrictions]);

  const dueDateClasses = [classes.DueDate];
  const amountClasses = [classes.Amount];

  if (accountInfo?.dueDate && isPast(new Date(accountInfo?.dueDate))) {
    dueDateClasses.push(classes.Error);
    amountClasses.push(classes.Error);
  }

  const autoPayDateWithLabel = (data: Date) => {
    const date = data as unknown as string;
    if (date.toLocaleLowerCase().endsWith("z")) {
      return `${t("common:autopay_label")}: ${t("common:date_format", {
        value: date.substring(0, date.length - 1),
      })}`;
    } else {
      return `${t("common:autopay_label")}: ${t("common:date_format", {
        value: data,
      })}`;
    }
  };

  const autoPayCancelWithLabel = (data: string) => {
    return `${t("common:autopay_cancel")} ${t("common:date_format", {
      value: data,
    })}`;
  };

  const convertCronToDate = (cronExp: string) => {
    const interval = parseExpression(cronExp);
    return interval.next().toDate();
  };

  const drawAutoPayDate = () => {
    if (autoPayInfo?.status === 1) {
      // recurring payments are enabled
      if (autoPayInfo.recurrenceScheduleType === 2) {
        // due date
        if (!!schedulePayments && schedulePayments.length > 0) {
          // There are upcoming scheduled payments
          const index = schedulePayments.findIndex(
            (p) => p.recurrenceScheduleId === autoPayInfo.id,
          );

          if (index < 0) {
            // The upcoming scheduled payment is a one-time payment and not a recurring payment.
            if (isPast(new Date(accountInfo?.dueDate!))) {
              // The due date is in the past; show nothing.
              return "";
            } else {
              // This means the user has cancelled.
              return autoPayCancelWithLabel(accountInfo?.dueDate!);
            }
          } else {
            // An upcoming scheduled payment is for the recurring payment.
            // Show that the auto pay will happen.
            return autoPayDateWithLabel(
              schedulePayments[index]?.scheduledPaymentDate,
            );
          }
        } else if (isPast(new Date(accountInfo?.dueDate!))) {
          // Today's date is past the account's due date. There are no scheduled payments
          return "";
        } else {
          // There are no scheduled payments and the account's due date is in the future.
          // This means the user has cancelled.
          return autoPayCancelWithLabel(accountInfo?.dueDate!);
        }
      } else {
        // specific date
        const nextScheduledDate = convertCronToDate(
          autoPayInfo.scheduleExpression,
        );

        if (!!schedulePayments && schedulePayments.length > 0) {
          // there are scheduled payments; they could be for the reccurence or a one-time scheduled payment
          const index = schedulePayments.findIndex(
            (p) => p.recurrenceScheduleId === autoPayInfo.id,
          );

          if (index < 0) {
            // The upcoming scheduled payment is a one-time payment and not a recurring payment.
            return autoPayCancelWithLabel(nextScheduledDate.toDateString());
          } else {
            // An upcoming scheduled payment is for the recurring payment.
            // Show that the auto pay will happen.
            return autoPayDateWithLabel(
              schedulePayments[index]?.scheduledPaymentDate,
            );
          }
        } else {
          // no currently scheduled payments
          const today = dateFnsSet(new Date(), {
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0,
          });
          const todaysPayment = payments.find((p) => {
            return isEqual(new Date(p.paymentDate), today);
          });

          if (!todaysPayment) {
            // There wasn't a payment made today - at least there wasn't a payment that was made for the "date"
            if (isEqual(addMonths(nextScheduledDate, -1), today)) {
              // the current scheduled date is actually today
              // and we have no payment made today
              // assume it was cancelled.
              return autoPayCancelWithLabel(today.toDateString());
            } else {
              /*
              This is one of the interesting cases. The next scheduled 
              date is in the future. There is no scheduled payment. This
              could be for a couple of reasons:
              1. The user cancelled the payment.
              2. The amount due is $0
              */

              if (accountInfo?.amountDue === 0) {
                return "";
              } else {
                return autoPayCancelWithLabel(nextScheduledDate.toDateString());
              }
            }
          }
        }
      }

      if (schedulePayments && schedulePayments[0]?.scheduledPaymentDate) {
        // If auto-pay is enabled, we have scheduled payments, and the first one has a scheduled payment date.
        return autoPayDateWithLabel(schedulePayments[0]?.scheduledPaymentDate);
      } else if (
        autoPayInfo?.scheduledPayments &&
        autoPayInfo?.scheduledPayments.length === 0
      ) {
        // no currently scheduled payments
        const today = dateFnsSet(new Date(), {
          hours: 0,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        });
        const todaysPayment = payments.find((p) => {
          return isEqual(new Date(p.paymentDate), today);
        });

        if (!todaysPayment) {
          // There wasn't a payment made today - at least there wasn't a payment that was made for the "date"
          if (!!autoPayInfo.scheduleExpression) {
            const scheduledDate = convertCronToDate(
              autoPayInfo.scheduleExpression,
            );

            if (!isEqual(addMonths(scheduledDate, -1), today)) {
              return autoPayCancelWithLabel(scheduledDate.toDateString());
            }
          } else {
            return autoPayCancelWithLabel(accountInfo?.dueDate!);
          }
        }
      }
    }

    return "";
  };

  return (
    <div className={classes.Wrapper}>
      {accountInfo ? (
        <>
          <div
            className={[classes.DetailContainer, classes.WidgetMargin].join(
              " ",
            )}
          >
            <div className={classes.DetailInfo}>
              <div className={classes.PayeeDetail}>
                <p className={classes.PayeeName}>{applicationName}</p>
                <p
                  data-testid="account-number-dashboard"
                  className={classes.PayeeAccount}
                >
                  {accountInfo?.accountNumber}
                </p>
              </div>
              <div className={classes.ExtraDetail}>
                <p className={classes.DueDateWrapper}>
                  {t("common:due_date")}:
                  <span className={dueDateClasses.join(" ")}>
                    {" " +
                      t("common:date_format", {
                        value: accountInfo?.dueDate,
                      })}
                  </span>
                </p>
                <p className={classes.AutoPayWrapper}>
                  {t("common:autopay_label")}:{" "}
                  <span
                    className={
                      autoPayInfo?.status === 1
                        ? classes.AutoPayOn
                        : classes.AutoPayOff
                    }
                  >
                    {/* TODO: Check for auto pay if its comming from the service or not after autopay implemented. */}
                    {autoPayInfo?.status === 1
                      ? t("common:on")
                      : t("common:off")}
                  </span>
                </p>
              </div>
            </div>
            <div className={classes.DetailPayment}>
              <div className={classes.Details}>
                <p className={classes.Label}>{t("common:amount_due")}</p>
                <figure className={amountClasses.join(" ")}>
                  {t("common:currency_format", {
                    value: accountInfo?.amountDue,
                  })}
                </figure>

                <p className={classes.AutoPayDate}>{drawAutoPayDate()}</p>
              </div>
            </div>
          </div>
          <div className={classes.ButtonWrapper}>
            <ft-button
              class={[classes.Autopay, "outline-button"].join(" ")}
              label={t("common:manage_autopay")}
              data-testid="ManageAutopay-open-dashboard"
              outlined
              disabled={actionDisabled ? actionDisabled : null}
              onClick={() => {
                dispatch(updateSlide({ id: "ManageWallet", tab: 1 }));
              }}
            ></ft-button>
            <ft-button
              class={classes.Make_payment}
              label={t("common:make_payment_label")}
              unelevated
              disabled={actionDisabled ? actionDisabled : null}
              data-testid="dashboard-make-payment-button"
              onClick={() => {
                dispatch(updateSlide({ id: "MakeAPayment" }));
              }}
            ></ft-button>
          </div>
        </>
      ) : (
        <div className={classes.LoaderWrapper}>
          <ft-loader
            styleOverrides={JSON.stringify({ height: "20px", width: "40%" })}
            count={1}
          ></ft-loader>
          <ft-loader
            styleOverrides={JSON.stringify({ height: "35px" })}
            count={3}
          ></ft-loader>
        </div>
      )}
    </div>
  );
};
